import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { TokenInfo } from '@uniswap/token-lists'
import { useMemo } from 'react'

/** Sorts currency amounts (descending). */
function balanceComparator(a?: CurrencyAmount<Currency>, b?: CurrencyAmount<Currency>) {
  if (a && b) {
    return a.greaterThan(b) ? -1 : a.equalTo(b) ? 0 : 1
  } else if (a?.greaterThan('0')) {
    return -1
  } else if (b?.greaterThan('0')) {
    return 1
  }
  return 0
}

type TokenBalances = { [tokenAddress: string]: CurrencyAmount<Token> | undefined }
type TokenPrices = { [tokenAddress: string]: number | null | undefined }

/** Sorts tokens by currency amount (descending), then symbol (ascending). */
export function tokenComparator(balances: TokenBalances, tokenPrices: TokenPrices, a: Token, b: Token) {
  const balanceA = balances[a?.address]
  const balanceB = balances[b?.address]
  const priceA = tokenPrices[a?.address] ?? 0
  const priceB = tokenPrices[b?.address] ?? 0

  // Calculate USD values
  const usdValueA = balanceA && priceA > 0 ? parseFloat(balanceA.toExact()) * priceA : 0
  const usdValueB = balanceB && priceB > 0 ? parseFloat(balanceB.toExact()) * priceB : 0

  // Priority 1: Tokens with significant USD value (> $1)
  const hasSignificantValueA = usdValueA > 1
  const hasSignificantValueB = usdValueB > 1

  if (hasSignificantValueA && !hasSignificantValueB) return -1
  if (!hasSignificantValueA && hasSignificantValueB) return 1

  // Priority 2: Among significant tokens, sort by USD value
  if (hasSignificantValueA && hasSignificantValueB) {
    return usdValueB - usdValueA
  }

  // Priority 3: Tokens with any balance but low value
  const hasBalanceA = balanceA && !balanceA.equalTo('0')
  const hasBalanceB = balanceB && !balanceB.equalTo('0')

  if (hasBalanceA && !hasBalanceB) return -1
  if (!hasBalanceA && hasBalanceB) return 1

  // Priority 4: Among tokens with balances, prefer higher USD value
  if (hasBalanceA && hasBalanceB) {
    if (usdValueA !== usdValueB) {
      return usdValueB - usdValueA
    }
    // Fallback to balance comparison if USD values are equal
    return balanceComparator(balanceA, balanceB)
  }

  // Priority 5: For tokens without balances, prefer tokens with higher prices
  if (priceA !== priceB) {
    return priceB - priceA
  }

  // Final fallback: alphabetical by symbol
  if (a?.symbol && b?.symbol) {
    return a.symbol.toLowerCase() < b.symbol.toLowerCase() ? -1 : 1
  }

  return -1
}

export function basicTokenComparator(balances: TokenBalances, a: Token, b: Token) {
  const balanceComparison = balanceComparator(balances[a?.address], balances[b?.address])
  if (balanceComparison !== 0) return balanceComparison

  if (a?.symbol && b?.symbol) {
    return a.symbol.toLowerCase() < b.symbol.toLowerCase() ? -1 : 1
  }

  return -1
}

/** Sorts tokens by query, giving precedence to exact matches and partial matches. */
export function useSortTokensByQuery<T extends Token | TokenInfo>(query: string, tokens?: T[]): T[] {
  return useMemo(() => {
    if (!tokens) {
      return []
    }

    const matches = query
      .toLowerCase()
      .split(/\s+/)
      .filter((s) => s.length > 0)

    if (matches.length > 1) {
      return tokens
    }

    const exactMatches: T[] = []
    const symbolSubtrings: T[] = []
    const rest: T[] = []

    // sort tokens by exact match -> subtring on symbol match -> rest
    tokens.map((token) => {
      if (token.symbol?.toLowerCase() === matches[0]) {
        return exactMatches.push(token)
      } else if (token.symbol?.toLowerCase().startsWith(query.toLowerCase().trim())) {
        return symbolSubtrings.push(token)
      } else {
        return rest.push(token)
      }
    })

    return [...exactMatches, ...symbolSubtrings, ...rest]
  }, [tokens, query])
}

export function filterLowValueTokens(
  tokens: Token[],
  balances: TokenBalances,
  tokenPrices: TokenPrices,
  minUsdValue = 0.01,
  pricesLoading = false
): Token[] {
  return tokens.filter((token) => {
    const balance = balances[token.address]
    const price = tokenPrices[token.address]

    if (!balance || balance.equalTo('0')) return true

    if (pricesLoading) return true

    if (price === null || price === undefined) {
      return true
    }

    if (price === 0) {
      return true
    }

    try {
      const usdValue = parseFloat(balance.toExact()) * price
      return usdValue >= minUsdValue
    } catch (error) {
      return true
    }
  })
}
